import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useEffect, useId, useRef } from "react";
import { ControlPosition, IControl, useControl, useMap } from "react-map-gl/maplibre";
import { arr } from "shared/utils/array";
import { zx } from "shared/utils/zx";

type MapDrawControlProps = {
    position?: ControlPosition;
    features: GeoJSON.Feature<GeoJSON.Polygon>[];
    onChange?: (features: GeoJSON.Feature<GeoJSON.Polygon>[]) => unknown;
};

export function MapPolygonDrawOverlay(props: MapDrawControlProps) {
    const map = useMap();
    const id = useId();

    const ctrl = useRef(
        new MapboxDraw({
            displayControlsDefault: false,
            styles: [
                {
                    id: "gl-draw-polygon-fill-inactive-" + id,
                    type: "fill",
                    source: "mapbox-gl-draw-cold",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "false"], ["==", "active", false]]],
                    paint: { "fill-color": "#3bb2d0", "fill-opacity": 0.18 },
                },
                {
                    id: "gl-draw-polygon-stroke-inactive-" + id,
                    type: "line",
                    source: "mapbox-gl-draw-cold",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "false"], ["==", "active", false]]],
                    paint: { "line-color": "#3bb2d0", "line-width": 2 },
                },
                {
                    id: "gl-draw-polygon-fill-active-cold-" + id,
                    type: "fill",
                    source: "mapbox-gl-draw-cold",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "true"], ["==", "active", true]]],
                    paint: { "fill-color": "#fbb03b", "fill-opacity": 0.25 },
                },
                {
                    id: "gl-draw-polygon-stroke-active-cold-" + id,
                    type: "line",
                    source: "mapbox-gl-draw-cold",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "true"], ["==", "active", true]]],
                    paint: { "line-color": "#fbb03b", "line-width": 3 },
                },
                {
                    id: "gl-draw-polygon-fill-inactive-hot-" + id,
                    type: "fill",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "false"], ["==", "active", false]]],
                    paint: { "fill-color": "#3bb2d0", "fill-opacity": 0.18 },
                },
                {
                    id: "gl-draw-polygon-stroke-inactive-hot-" + id,
                    type: "line",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "false"], ["==", "active", false]]],
                    paint: { "line-color": "#3bb2d0", "line-width": 2 },
                },
                {
                    id: "gl-draw-polygon-fill-active-" + id,
                    type: "fill",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "true"], ["==", "active", true]]],
                    paint: { "fill-color": "#fbb03b", "fill-opacity": 0.25 },
                },
                {
                    id: "gl-draw-polygon-stroke-active-" + id,
                    type: "line",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "$type", "Polygon"], ["any", ["==", "active", "true"], ["==", "active", true]]],
                    paint: { "line-color": "#fbb03b", "line-width": 3 },
                },
                {
                    id: "gl-draw-polygon-and-line-vertex-halo-active-" + id,
                    type: "circle",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
                    paint: {
                        "circle-radius": 6,
                        "circle-color": "#fff",
                    },
                },
                {
                    id: "gl-draw-polygon-and-line-vertex-active-" + id,
                    type: "circle",
                    source: "mapbox-gl-draw-hot",
                    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
                    paint: {
                        "circle-radius": 4,
                        "circle-color": "#fbb03b",
                    },
                },
            ],
            controls: {
                trash: true,
                polygon: true,
            },
        }),
    );

    function getMapFeatures() {
        return ctrl.current.getAll().features.map((it) => zx.geo.feature<GeoJSON.Polygon>(zx.geo.polygon()).parse(it));
    }

    useEffect(() => {
        function handleChange() {
            props.onChange?.(getMapFeatures());
        }

        function handleModeChange(evt: { mode?: string }) {
            if (evt.mode !== "direct_select") return;
            if (ctrl.current.getSelectedPoints().features.length > 0) return;

            const featureIds = ctrl.current.getSelectedIds();
            if (featureIds.length == 0) return;

            // Keep polygon selection in simple_select when no vertex is selected
            // so the trash control reliably deletes the selected polygon.
            ctrl.current.changeMode("simple_select", { featureIds });
        }

        map.current?.on("draw.create", handleChange);
        map.current?.on("draw.update", handleChange);
        map.current?.on("draw.delete", handleChange);
        map.current?.on("draw.modechange", handleModeChange);

        return () => {
            map.current?.off("draw.create", handleChange);
            map.current?.off("draw.update", handleChange);
            map.current?.off("draw.delete", handleChange);
            map.current?.off("draw.modechange", handleModeChange);
        };
    }, [map.current]);

    useControl(
        () => ctrl.current as IControl,
        () => {
            props.features.forEach(ctrl.current.add);
        },
        () => {},
        {
            position: props.position,
        },
    );

    useEffect(() => {
        if (arr(props.features).eqBy("id").congruentTo(getMapFeatures())) return;

        // replace with incoming features

        ctrl.current.deleteAll();
        props.features.forEach(ctrl.current.add);
    }, [ctrl, JSON.stringify(props.features)]);

    return null;
}
